/**
 * #6381 step B: the ONE door the screens use to read QuickBooks mirror data.
 *
 * The browser used to read `qbo_*` collections straight from Firestore, which any employee's session could do. These
 * calls go to server endpoints that check who is asking and which company's books they may see (see
 * functions/src/qboRead). Rows come back as plain objects; a Firestore Timestamp travels as `{__ts:{s,n}}` and is
 * turned back into a real Timestamp here, once, so a screen sees the same shapes it always did.
 */
import { Timestamp } from 'firebase/firestore';
import { httpsCallable } from '../firebase/authReadyCallable';
import { functions } from '../../config/firebase';

export type QboReadCallable =
  | 'finance_qboRead'
  | 'projects_qboRead'
  | 'support_getRelatedBilling'
  | 'fieldService_qboRead'
  | 'dataHub_readQboTable'
  | 'customers_qboRead';

/** A row as the server sends it: the document's fields plus `id` (the document id). */
export type QboRow = { id: string } & Record<string, unknown>;

/** Where the next page starts; handed back to the server unchanged. */
export interface QboCursor {
  v: unknown;
  id: string;
}

export interface QboPage {
  rows: QboRow[];
  nextCursor: QboCursor | null;
}

const MAX_DEPTH = 12;

function isWireTimestamp(value: unknown): value is { __ts: { s: number; n: number } } {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const keys = Object.keys(value);
  const inner = (value as { __ts?: unknown }).__ts;
  return keys.length === 1 && keys[0] === '__ts' && !!inner && typeof inner === 'object'
    && typeof (inner as { s?: unknown }).s === 'number' && typeof (inner as { n?: unknown }).n === 'number';
}

/** Turn every `{__ts:{s,n}}` in a server answer back into a Firestore Timestamp, at any depth. */
export function rehydrate<T = unknown>(value: unknown, depth = 0): T {
  if (depth > MAX_DEPTH) throw new Error('QuickBooks answer is nested too deeply to read');
  if (isWireTimestamp(value)) return new Timestamp(value.__ts.s, value.__ts.n) as unknown as T;
  if (Array.isArray(value)) return value.map((v) => rehydrate(v, depth + 1)) as unknown as T;
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[k] = rehydrate(v, depth + 1);
    return out as T;
  }
  return value as T;
}

/** One call. Errors are the server's own (permission-denied, resource-exhausted, ...): never swallowed here. */
export async function qboRead<T = unknown>(callable: QboReadCallable, payload: Record<string, unknown>): Promise<T> {
  // The server reads ALL of the org's companies for an empty list. A caller that meant "none" must not widen its scope.
  if (Array.isArray(payload.realmIds) && payload.realmIds.length === 0) {
    throw new Error('realmIds must not be an empty list: leave it out to read every company of the org');
  }
  const fn = httpsCallable<Record<string, unknown>, unknown>(functions, callable);
  const result = await fn(payload);
  const data = result.data;
  // The cursor is handed back to the server exactly as received: a Timestamp inside it must stay `{__ts:{s,n}}`.
  if (data && typeof data === 'object' && !Array.isArray(data) && 'nextCursor' in data) {
    const { nextCursor, ...rest } = data as Record<string, unknown>;
    return { ...rehydrate<Record<string, unknown>>(rest), nextCursor } as T;
  }
  return rehydrate<T>(data);
}

/**
 * Every page of a paged op. Running out of pages is an ERROR, never a short list presented as the whole:
 * `maxPages` is a ceiling the caller chose on purpose.
 */
export async function qboReadAllPages(
  callable: QboReadCallable,
  payload: Record<string, unknown>,
  maxPages: number,
  /** React Query's abort signal: a screen that is left stops asking the server for the remaining pages. */
  signal?: AbortSignal
): Promise<QboRow[]> {
  const rows: QboRow[] = [];
  let cursor: QboCursor | null = null;
  for (let page = 0; page < maxPages; page += 1) {
    if (signal?.aborted) throw new Error('QuickBooks read cancelled');
    const out: QboPage = await qboRead<QboPage>(callable, cursor ? { ...payload, cursor } : payload);
    rows.push(...out.rows);
    if (!out.nextCursor) return rows;
    cursor = out.nextCursor;
  }
  throw new Error(`QuickBooks list is longer than ${maxPages} pages; refusing to show a partial list`);
}

/**
 * Connection states whose copied data the server serves for an org (server: functions/src/qboRead/orgRealms.ts). A
 * company in any other state (disconnected) is refused by the server, so a screen must not name it.
 */
export const SERVED_QBO_STATUSES: ReadonlyArray<string> = ['active', 'expired', 'error'];
export const isServedQboStatus = (status: string | null | undefined): boolean => !!status && SERVED_QBO_STATUSES.includes(status);

/** What a QuickBooks mirror document id looks like (`realmId_id`): anything else makes the server refuse a whole batch. */
export const QBO_DOC_ID_SHAPE = /^[A-Za-z0-9]{1,40}_[A-Za-z0-9_-]{1,150}$/;

/** The most companies one server query covers (Firestore allows 30 values in an `in` filter). */
export const REALMS_PER_CALL = 30;

/** Ceiling for customers_qboRead listActive (1,000 a page). Hitting it is an error from qboReadAllPages, never a short
 * list; it is a guard, not an expected size. */
export const QBO_CUSTOMER_MAX_PAGES = 20;

/** Ceiling for customers_qboRead vendorNames (1,000 a page). Hitting it is an error from qboReadAllPages, never a short
 * list; it is a guard, not an expected size. */
export const QBO_VENDOR_MAX_PAGES = 10;

/**
 * Like qboReadAllPages, but for a list of companies longer than the server accepts at once: companies are read in
 * groups of 30, one group after another, and the rows are joined. No companies named = the server's "all of the org's
 * companies" (the server refuses an org with more than 30 in that form, out loud).
 */
export async function qboReadAllPagesByRealm(
  callable: QboReadCallable,
  payload: Record<string, unknown>,
  realmIds: string[],
  maxPages: number
): Promise<QboRow[]> {
  if (realmIds.length === 0) return qboReadAllPages(callable, payload, maxPages);
  const rows: QboRow[] = [];
  for (let i = 0; i < realmIds.length; i += REALMS_PER_CALL) {
    rows.push(...await qboReadAllPages(callable, { ...payload, realmIds: realmIds.slice(i, i + REALMS_PER_CALL) }, maxPages));
  }
  return rows;
}

/** A row dressed as the Firestore snapshot the older code expects (`id` and `data()`). */
export interface QboSnap {
  id: string;
  data: () => Record<string, unknown>;
}

export function asSnapshot(row: QboRow): QboSnap {
  const { id, ...rest } = row;
  return { id, data: () => rest };
}
